import { Flex, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { homeColor, homeText } from "../home-theme.ts"
import { type UserInfo } from "../../LoginPage/types/auth.types.ts"
import {
  DashboardHeaderSearch,
  type DashboardTableFilterKey
} from "./DashboardHeaderSearch.tsx"

const { Text } = Typography

interface DashboardHeaderProps {
  user: UserInfo | null
  canSearch: boolean
  canCreate: boolean
  tableFilterKey: DashboardTableFilterKey | null
  /** My View / Edit Layout, which join the one toolbar. */
  viewControls?: React.ReactNode
  /** Export, at the toolbar's end. */
  exportControl?: React.ReactNode
}

/**
 * One compact row: the greeting on the left, every control in a single tidy
 * toolbar on the right. No banner, no illustration — they cost ~130px of the
 * fold and said nothing.
 */
export function DashboardHeader({
  user,
  canCreate,
  canSearch,
  tableFilterKey,
  viewControls,
  exportControl
}: DashboardHeaderProps) {
  const { t } = useTranslation()
  const name = user?.first_name

  return (
    <Flex
      gap={16}
      wrap="wrap"
      align="center"
      justify="space-between"
      className={"max-lg:flex-col max-lg:items-start"}
    >
      <div className={"min-w-0"}>
        <h1
          className="m-0 text-start"
          style={{ ...homeText.welcomeTitle, color: homeColor.ink }}
        >
          {name
            ? t("dashboard.greeting", "Welcome, {{name}}!", { name })
            : t("dashboard.greetingNoName", "Welcome!")}
        </h1>
        <Text style={{ ...homeText.welcomeSubtitle, color: homeColor.inkMuted }}>
          {t(
            "dashboard.subtitle",
            "Here's a snapshot of what's happening today."
          )}
        </Text>
      </div>

      <Flex gap={10} wrap="wrap" align="center" className={"max-lg:w-full"}>
        {viewControls}
        {(canSearch || canCreate) && tableFilterKey && (
          <DashboardHeaderSearch
            canCreate={canCreate}
            tableFilterKey={tableFilterKey}
          />
        )}
        {exportControl}
      </Flex>
    </Flex>
  )
}
